/**
 * TMDB (The Movie Database) API client
 */

export interface Movie {
  id: number
  title: string
  poster_path: string | null
  release_date: string
  overview: string
  vote_average: number
  media_type?: 'movie'
}

export interface TVShow {
  id: number
  name: string
  poster_path: string | null
  first_air_date: string
  overview: string
  vote_average: number
  media_type?: 'tv'
}

// Unified type for both movies and TV shows
// Note: TV shows are mapped to have 'title' and 'release_date' for unified display
export type MediaItem = (Movie & {media_type: 'movie'}) | (Omit<TVShow, 'name' | 'first_air_date'> & {
  title: string
  release_date: string
  media_type: 'tv'
})

export interface TMDBSearchResponse {
  page: number
  results: Movie[]
  total_pages: number
  total_results: number
}

export interface TMDBSearchTVResponse {
  page: number
  results: TVShow[]
  total_pages: number
  total_results: number
}

// TMDB credentials - check environment variables first, then fallback to API key
const TMDB_API_KEY = process.env.EXPO_PUBLIC_TMDB_API_KEY || 'e69848561c027d2aa5ece094ac352acc'
const TMDB_ACCESS_TOKEN = process.env.EXPO_PUBLIC_TMDB_ACCESS_TOKEN || 'eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJlNjk4NDg1NjFjMDI3ZDJhYTVlY2UwOTRhYzM1MmFjYyIsIm5iZiI6MTc2MzkxNDEwNy4wMzMsInN1YiI6IjY5MjMzMTdiMzE2NTNmYjZjNzE4NzFiNCIsInNjb3BlcyI6WyJhcGlfcmVhZCJdLCJ2ZXJzaW9uIjoxfQ.BgMSVF_NkNIPvijuiWDzIStKOx_rxkaXc1E7Dy6ygHU'
const TMDB_BASE_URL = 'https://api.themoviedb.org/3'

/**
 * Search for movies and TV shows using TMDB API
 * @param query - Search query string
 * @param signal - AbortSignal for request cancellation
 * @returns Promise with array of movies and TV shows
 */
export async function searchMovies(
  query: string,
  signal?: AbortSignal,
): Promise<MediaItem[]> {
  // Prefer access token over API key (token is more secure)
  if (!TMDB_ACCESS_TOKEN && !TMDB_API_KEY) {
    throw new Error('TMDB_ACCESS_TOKEN or TMDB_API_KEY must be configured')
  }

  if (!query.trim()) {
    return []
  }

  const trimmedQuery = query.trim()
  
  // Use Bearer token if available, otherwise fall back to API key in query parameter
  const headers: HeadersInit = {}
  if (TMDB_ACCESS_TOKEN) {
    headers['Authorization'] = `Bearer ${TMDB_ACCESS_TOKEN}`
  }

  // Search both movies and TV shows in parallel
  const movieUrl = `${TMDB_BASE_URL}/search/movie?query=${encodeURIComponent(
    trimmedQuery,
  )}&include_adult=false&language=en-US&page=1${!TMDB_ACCESS_TOKEN ? `&api_key=${TMDB_API_KEY}` : ''}`

  const tvUrl = `${TMDB_BASE_URL}/search/tv?query=${encodeURIComponent(
    trimmedQuery,
  )}&include_adult=false&language=en-US&page=1${!TMDB_ACCESS_TOKEN ? `&api_key=${TMDB_API_KEY}` : ''}`

  const [movieResponse, tvResponse] = await Promise.all([
    fetch(movieUrl, {signal, headers}),
    fetch(tvUrl, {signal, headers}),
  ])

  if (!movieResponse.ok) {
    throw new Error(
      `TMDB API error (movies): ${movieResponse.status} ${movieResponse.statusText}`,
    )
  }

  if (!tvResponse.ok) {
    throw new Error(
      `TMDB API error (TV): ${tvResponse.status} ${tvResponse.statusText}`,
    )
  }

  const movieData = (await movieResponse.json()) as TMDBSearchResponse
  const tvData = (await tvResponse.json()) as TMDBSearchTVResponse

  // Combine results and mark with media_type
  const movies: MediaItem[] = (movieData.results || []).map(movie => ({
    ...movie,
    media_type: 'movie' as const,
  }))

  const tvShows: MediaItem[] = (tvData.results || []).map(tv => ({
    id: tv.id,
    title: tv.name, // Map 'name' to 'title' for unified display
    poster_path: tv.poster_path,
    release_date: tv.first_air_date, // Map 'first_air_date' to 'release_date' for unified display
    overview: tv.overview,
    vote_average: tv.vote_average,
    media_type: 'tv' as const,
  }))

  // Combine and sort by vote_average (highest first)
  return [...movies, ...tvShows].sort((a, b) => b.vote_average - a.vote_average)
}

/**
 * Get poster image URL from TMDB poster_path
 */
export function getPosterUrl(posterPath: string | null): string | null {
  if (!posterPath) return null
  return `https://image.tmdb.org/t/p/w342${posterPath}`
}

export interface TVSeason {
  air_date: string | null
  episode_count: number
  id: number
  name: string
  overview: string
  poster_path: string | null
  season_number: number
}

export interface TVEpisode {
  air_date: string | null
  episode_number: number
  id: number
  name: string
  overview: string
  production_code: string
  runtime: number | null
  season_number: number
  show_id: number
  still_path: string | null
  vote_average: number
  vote_count: number
}

export interface TVDetailsResponse {
  id: number
  name: string
  seasons: TVSeason[]
}

export interface TVSeasonResponse {
  id: string
  air_date: string | null
  episodes: TVEpisode[]
  name: string
  overview: string
  poster_path: string | null
  season_number: number
}

/**
 * Get TV show details including seasons
 */
export async function getTvSeasons(
  tmdbId: number,
  signal?: AbortSignal,
): Promise<TVSeason[]> {
  if (!TMDB_ACCESS_TOKEN && !TMDB_API_KEY) {
    throw new Error('TMDB_ACCESS_TOKEN or TMDB_API_KEY must be configured')
  }

  const headers: HeadersInit = {}
  if (TMDB_ACCESS_TOKEN) {
    headers['Authorization'] = `Bearer ${TMDB_ACCESS_TOKEN}`
  }

  const url = `${TMDB_BASE_URL}/tv/${tmdbId}?language=en-US${!TMDB_ACCESS_TOKEN ? `&api_key=${TMDB_API_KEY}` : ''}`

  const response = await fetch(url, {signal, headers})

  if (!response.ok) {
    throw new Error(
      `TMDB API error: ${response.status} ${response.statusText}`,
    )
  }

  const data = (await response.json()) as TVDetailsResponse
  return data.seasons || []
}

/**
 * Get episodes for a specific TV season
 */
export async function getSeasonEpisodes(
  tmdbId: number,
  seasonNumber: number,
  signal?: AbortSignal,
): Promise<TVEpisode[]> {
  if (!TMDB_ACCESS_TOKEN && !TMDB_API_KEY) {
    throw new Error('TMDB_ACCESS_TOKEN or TMDB_API_KEY must be configured')
  }

  const headers: HeadersInit = {}
  if (TMDB_ACCESS_TOKEN) {
    headers['Authorization'] = `Bearer ${TMDB_ACCESS_TOKEN}`
  }

  const url = `${TMDB_BASE_URL}/tv/${tmdbId}/season/${seasonNumber}?language=en-US${!TMDB_ACCESS_TOKEN ? `&api_key=${TMDB_API_KEY}` : ''}`

  const response = await fetch(url, {signal, headers})

  if (!response.ok) {
    throw new Error(
      `TMDB API error: ${response.status} ${response.statusText}`,
    )
  }

  const data = (await response.json()) as TVSeasonResponse
  return data.episodes || []
}
